/**
 * The minimal coupon shape the PDP price preview needs — a subset of the API
 * `CouponItem` (`@ps/api/coupon`). `discountAmount` is in whole currency units
 * (e.g. `5` = $5 off), matching the API + RN.
 */
export interface PreviewCoupon {
  type: 'discountPercent' | 'discountAmount' | 'freeShipping';
  /** For `discountPercent` — 0..100. */
  discountPercent?: number;
  /** Optional cap on a percent discount (currency units). */
  maximumDiscount?: number;
  /** For `discountAmount` — whole currency units off. */
  discountAmount?: number;
  /**
   * The coupon must not stack on the listing's own sale price (auto-selling
   * offer coupons) — see `couponPreviewPrice`.
   */
  nonStackingWithListingDiscount?: boolean;
}

/**
 * Apply coupons to a single unit price for **display only** — mirrors
 * PopshopRN's `applyCouponsToUnitPrice`. Percent coupons discount by
 * `unitPrice * pct/100` (capped at `maximumDiscount` when set); amount coupons
 * subtract `discountAmount`; free-shipping has no product-price impact.
 *
 * Returns the discounted price, or `undefined` when there's no positive
 * discount — so the caller keeps its normal (non-coupon) render path. This is
 * NOT the source of truth for the charge: the checkout/server computes the
 * actual discount at purchase. It only powers the strikethrough preview.
 */
export function applyCouponsToUnitPrice(
  unitPrice: number,
  coupons: readonly PreviewCoupon[] | undefined,
): number | undefined {
  if (!coupons?.length || !Number.isFinite(unitPrice) || unitPrice <= 0) {
    return undefined;
  }

  let discount = 0;
  for (const coupon of coupons) {
    if (coupon.type === 'discountPercent' && coupon.discountPercent != null) {
      const raw = (unitPrice * coupon.discountPercent) / 100;
      discount +=
        coupon.maximumDiscount != null
          ? Math.min(raw, coupon.maximumDiscount)
          : raw;
    } else if (
      coupon.type === 'discountAmount' &&
      coupon.discountAmount != null
    ) {
      discount += coupon.discountAmount;
    }
    // freeShipping: no product-price impact.
  }

  const discounted = Math.max(0, unitPrice - discount);
  return discounted < unitPrice ? discounted : undefined;
}
